import { useQuery } from "@tanstack/react-query";
import { Clock3, ShieldAlert } from "lucide-react";
import { useParams } from "react-router-dom";
import { ApiRequestError } from "../../lib/errors";
import { getPublicFamilyStatus } from "../../lib/services/familyStatus";

function formatDateTime(value: string): string {
  if (!value) return "Unavailable";
  return new Date(value).toLocaleString();
}

export function FamilyStatusPage() {
  const { token } = useParams<{ token: string }>();

  const statusQuery = useQuery({
    queryKey: ["family-status", token],
    queryFn: () => getPublicFamilyStatus(token ?? ""),
    enabled: Boolean(token),
    refetchInterval: (query) => {
      const value = query.state.data?.poll_interval_seconds ?? 30;
      return value * 1000;
    },
    retry: false,
  });

  const error =
    statusQuery.error instanceof ApiRequestError ? statusQuery.error : null;
  const unavailable =
    error?.status === 410 || error?.status === 404 || !token?.trim();

  if (statusQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading family status update...
        </div>
      </div>
    );
  }

  if (unavailable || !statusQuery.data) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 text-amber-700" size={20} />
            <div>
              <h1 className="text-xl font-bold text-amber-950">
                Status Link Unavailable
              </h1>
              <p className="mt-2 text-sm text-amber-900">
                This family status link has expired, was revoked, or is no
                longer available. Please contact the care team directly for the
                latest update.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const status = statusQuery.data;
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f6fbff,_#eef4fb_55%,_#e7eef6)] px-4 py-10">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.24em] text-slate-500">
            Family Status Portal
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">
            {status.status_label}
          </h1>
          <p className="mt-3 text-base text-slate-600">
            {status.status_message}
          </p>
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <dl className="grid gap-4 md:grid-cols-2">
            <div>
              <dt className="text-xs font-black uppercase tracking-widest text-slate-500">
                Facility
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {status.facility_name}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-black uppercase tracking-widest text-slate-500">
                Visit Date
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {status.survey_date}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-black uppercase tracking-widest text-slate-500">
                Last Updated
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {formatDateTime(status.updated_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-black uppercase tracking-widest text-slate-500">
                Link Expires
              </dt>
              <dd className="mt-1 text-sm font-semibold text-slate-900">
                {formatDateTime(status.expires_at)}
              </dd>
            </div>
          </dl>

          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <Clock3 size={16} className="mr-2 inline-block" />
            This page refreshes automatically every{" "}
            {status.poll_interval_seconds} seconds.
          </div>
        </section>
      </div>
    </div>
  );
}
